<template>
    <v-container v-if="reportSettings" class="pa-5" fluid>
        <ConfirmDialog
            :is-open="lastPalletDialog"
            :action-loading="addPalletLoading"
            @on-confirm="addPalletEanToList"
            @on-abort="closeLastPalletDialog"
        >
            <template #title>
                Paleta z surowcem
            </template>
            <template #content>
                Znaleziono ostatnio zaraportowaną paletę z papierem: <strong>{{ lastParameter }}</strong>.
                Czy chcesz kontynuować ze wskazanym surowcem?
            </template>
            <template #confirmButton>
                Kontynuuj
            </template>
        </ConfirmDialog>

        <ImagePreviewDialog
            :is-open="imagePreviewDialog"
            :file-preview-uri="filePreviewURI"
            @on-close="closeImagePreviewDialog"
        />

        <OrdersListDialog
            :is-open="ordersListDialog"
            :current-plate="currentPlate"
            @on-close="closeOrdersListDialog"
        />

        <PalletsDialog
            :is-open="palletsListDialog"
            :pallets-list="pallets"
            :report-id="existingReportId"
            @on-close="closePalletsListDialog"
        />

        <ReportMaterialReservationDialog
            :is-open="materialReservationDialog"
            :object-number="task.primaryNumber"
            :production-site-id="reportSettings.workspace.id"
            @on-close="closeMaterialReservationDialog"
        />

        <page-title>Nowy raport - {{ processName }} na {{ machineName }}</page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn text color="primary" class="pa-0" @click="close">
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="10" offset-xl="1">
                <v-container class="pa-0">
                    <v-row>
                        <v-col cols="12" lg="6">
                            <v-btn
                                v-if="hasTaskComments && !areTaskCommentsVisible"
                                color="error"
                                outlined
                                x-large
                                block
                                class="mb-3"
                                data-sentry-label="Pokaż uwagi do zadania"
                                @click="areTaskCommentsVisible = true"
                            >
                                Pokaż uwagi do zadania
                            </v-btn>

                            <v-alert
                                v-if="hasTaskComments"
                                v-model="areTaskCommentsVisible"
                                type="error"
                                prominent
                                dark
                                elevation="2"
                            >
                                <span class="text-uppercase">Uwagi do zadania</span>

                                <v-divider class="my-4" color="error" />

                                <div
                                    v-for="(comment, index) in currentPlate.comments"
                                    :key="index"
                                    class="mb-3"
                                >
                                    <div>
                                        {{ index + 1 }}. {{ comment.text }}
                                    </div>
                                    <div v-if="comment.createdBy || comment.createdAt" class="comment-metadata">
                                        <span v-if="comment.createdBy" class="comment-author">
                                            {{ comment.createdBy }}
                                        </span>
                                        <span
                                            v-if="comment.createdBy && comment.createdAt"
                                        >•</span>
                                        <span v-if="comment.createdAt">
                                            {{ comment.createdAt }}
                                        </span>
                                    </div>
                                </div>

                                <v-divider class="my-4" color="error" />
                                <v-btn
                                    depressed
                                    outlined
                                    large
                                    block
                                    class="mt-5"
                                    data-sentry-label="Potwierdzenie zapoznania się z komentarzem"
                                    @click="confirmTaskCommentsRead()"
                                >
                                    Potwierdzam zapoznanie się z komentarzem
                                </v-btn>
                            </v-alert>
                        </v-col>
                        <v-col v-if="currentPlate.reportItemPlateOrders.length > 0" cols="12" lg="6">
                            <v-alert
                                v-if="currentPlate.reportItemPlateOrders.length === 1 && currentPlate.reportItemPlateOrders[0].isComplaint === true"
                                type="warning"
                                color="warning"
                                icon="mdi-alert"
                                border="left"
                            >
                                Zamówienie reklamowane
                            </v-alert>
                            <v-btn
                                v-else-if="currentPlate.reportItemPlateOrders.length > 1 && currentPlate.reportItemPlateOrders.some(order => order.isComplaint === true)"
                                color="warning"
                                x-large
                                block
                                class="mb-3"
                                data-sentry-label="Pokaż uwagi do zadania"
                                @click="showOrdersListDialog()"
                            >
                                <v-icon left color="red">
                                    mdi-alert
                                </v-icon>
                                Zawiera zamówienia reklamowane (ZOBACZ)
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" lg="3">
                            <v-btn
                                block
                                color="warning"
                                dark
                                data-sentry-label="Zgłoś zapotrzebowanie materiału"
                                @click="showMaterialReservationDialog"
                            >
                                ZGŁOŚ ZAPOTRZEBOWANIE MATERIAŁU
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" lg="6">
                            <v-container class="pa-0">
                                <v-row>
                                    <v-col cols="12">
                                        <v-card>
                                            <v-toolbar flat dense color="white lighten-2">
                                                <v-toolbar-title>Parametry {{ task.type.name }}</v-toolbar-title>
                                            </v-toolbar>
                                            <v-divider />
                                            <v-card-text>
                                                <v-container class="pa-0">
                                                    <v-row>
                                                        <v-col v-if="hasMultiplePlates" cols="12">
                                                            <v-select
                                                                v-model="currentPlate"
                                                                :items="plates"
                                                                :label="plateVerboseName"
                                                                item-value="id"
                                                                return-object
                                                                outlined
                                                                hide-details
                                                            >
                                                                <template slot="selection" slot-scope="data">
                                                                    {{ plateVerboseName }} {{ data.item.plateNumber }}
                                                                </template>
                                                                <template slot="item" slot-scope="data">
                                                                    {{ plateVerboseName }} {{ data.item.plateNumber }}
                                                                </template>
                                                            </v-select>
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <table class="table table--simple">
                                                                <tbody>
                                                                    <tr>
                                                                        <th>Numer:</th>
                                                                        <td>{{ task.primaryNumber }}</td>
                                                                    </tr>
                                                                    <tr v-if="isTypeIdValid">
                                                                        <th>Produkt:</th>
                                                                        <td>{{ taskProductTypeName }}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th>Nakład:</th>
                                                                        <td>{{ currentPlate.amount }} szt.</td>
                                                                    </tr>
                                                                    <tr v-for="(item) in taskParameters" :key="item.id">
                                                                        <template v-if="item.name !== 'składki'">
                                                                            <th>
                                                                                {{ item.name | capitalize }}:
                                                                            </th>
                                                                            <td>{{ parseEmptyValue(item.value) }}</td>
                                                                        </template>
                                                                        <template v-else>
                                                                            <th>
                                                                                {{ item.name | capitalize }}:
                                                                            </th>
                                                                            <template v-if="item.value.length > 0">
                                                                                <td>
                                                                                    <p v-for="foldedSignature in item.value" :key="foldedSignature.id" class="ma-0 ">
                                                                                        <strong>Opis: {{ foldedSignature.description }}, ilość stron {{ foldedSignature.pages }}, ilość: {{ foldedSignature.quantity }}</strong>
                                                                                    </p>
                                                                                </td>
                                                                            </template>
                                                                            <template v-else>
                                                                                <td>
                                                                                    -
                                                                                </td>
                                                                            </template>
                                                                        </template>
                                                                    </tr>
                                                                    <tr>
                                                                        <th>Lokalizacja:</th>
                                                                        <td>
                                                                            <template v-if="hasLocalization">
                                                                                <p v-for="place in currentPlateWareStoragePlaces" :key="place.id" class="ma-0 ">
                                                                                    <strong>hala: {{ place.hall.name }}, rząd {{ place.ordinalNumber }}, palet: {{ place.count }}</strong>
                                                                                </p>
                                                                            </template>
                                                                            <template v-else>
                                                                                -
                                                                            </template>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                        <v-col cols="12" lg="6">
                            <v-container class="pa-0">
                                <v-row align="center">
                                    <v-col cols="12">
                                        <v-card>
                                            <v-card-text>
                                                <v-container class="pa-0">
                                                    <v-row justify="center">
                                                        <v-col cols="12">
                                                            <div class="qr-code-container">
                                                                <img src="@/assets/images/qr/qr-tracking-start.svg" class="qr-code hidden-sm-and-down">
                                                                <v-row justify="space-between">
                                                                    <v-col v-if="isPalletRequired" cols="2" class="mr-6">
                                                                        <v-badge
                                                                            :content="addedPaperPallets"
                                                                            :color="hasNoPallets ? 'warning' : 'success'"
                                                                            overlap
                                                                        >
                                                                            <v-btn
                                                                                data-sentry-label="Pokaż okno palet z papierem"
                                                                                @click="showPalletsListDialog"
                                                                            >
                                                                                <span> Papier </span>
                                                                            </v-btn>
                                                                        </v-badge>
                                                                    </v-col>
                                                                    <v-col cols="8">
                                                                        <v-btn
                                                                            :loading="startTrackingLoading"
                                                                            color="success"
                                                                            data-sentry-label="Rozpocznij/Rozpocznij przyrząd"
                                                                            :disabled="isStartButtonDisabled"
                                                                            @click="createReport"
                                                                        >
                                                                            {{ reportStartButtonText }}
                                                                        </v-btn>
                                                                    </v-col>
                                                                </v-row>
                                                            </div>
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                    <v-col v-if="additionalActionsProcessProductionSite.length > 0" cols="12">
                                        <v-card>
                                            <v-card-text>
                                                <v-data-table
                                                    :headers="additionalActionsProcessProductionSiteHeaders"
                                                    :items="additionalActionsProcessProductionSite"
                                                    disable-pagination
                                                    hide-default-footer
                                                    :mobile-breakpoint="0"
                                                    show-select
                                                    name="additionalActionsProcessProductionSiteData"
                                                >
                                                    <template #header.data-table-select>
                                                        <v-simple-checkbox
                                                            v-model="selectAll"
                                                            :ripple="false"
                                                            @input="toggleSelectAll($event)"
                                                        />
                                                    </template>
                                                    <template #item.data-table-select="{item}">
                                                        <v-simple-checkbox
                                                            v-model="item.isSelected"
                                                            :ripple="false"
                                                            @input="updateSelectAll()"
                                                        />
                                                    </template>
                                                </v-data-table>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-card>
                                <v-toolbar flat dense color="white lighten-2">
                                    <v-toolbar-title>Podgląd</v-toolbar-title>
                                </v-toolbar>

                                <v-divider />

                                <v-card-text>
                                    <v-container class="pa-0">
                                        <v-row v-viewer dense>
                                            <template v-if="hasPreviews">
                                                <v-col v-for="(components, key, index) in currentPlatePreviews" :key="index" cols="6">
                                                    <h3 class="text-subtitle-1 my-2">
                                                        {{ key }}
                                                    </h3>
                                                    <v-row>
                                                        <v-col cols="11">
                                                            <v-container>
                                                                <v-carousel
                                                                    v-model="carouselIndexes[index]"
                                                                    height="auto"
                                                                    hide-delimiters
                                                                    :show-arrows="false"
                                                                    :continuous="false"
                                                                    :dark="$vuetify.theme.dark"
                                                                    :light="!$vuetify.theme.dark"
                                                                    :touchless="true"
                                                                >
                                                                    <v-carousel-item
                                                                        v-for="(image, previewIndex) in components.filter((item, index2) => index2 % 2 == 0)"
                                                                        :key="previewIndex"
                                                                    >
                                                                        <v-container>
                                                                            <v-row justify="space-around">
                                                                                <v-icon :disabled="!(previewIndex > 0)" @click="prev(index)">
                                                                                    mdi-chevron-left-circle
                                                                                </v-icon>
                                                                                <template v-if="image.pageNumber">
                                                                                    {{ 'Strona ' + image.pageNumber }}
                                                                                </template>
                                                                                <v-icon :disabled="!(previewIndex * 2 + 1 < components.length - 1)" @click="next(index)">
                                                                                    mdi-chevron-right-circle
                                                                                </v-icon>
                                                                            </v-row>
                                                                            <v-row dense>
                                                                                <v-col cols="6">
                                                                                    <img
                                                                                        :src="taskPreviewURI(image)"
                                                                                        class="report-preview-img"
                                                                                        :alt="`Podgląd ${image.side == 1 ? 'A' : 'B'}`"
                                                                                    >
                                                                                </v-col>
                                                                                <v-col v-if="previewIndex * 2 + 1 < components.length" cols="6">
                                                                                    <img
                                                                                        :src="taskPreviewURI(components[previewIndex * 2 + 1])"
                                                                                        class="report-preview-img"
                                                                                        :alt="`Podgląd ${components[previewIndex * 2 + 1].side == 1 ? 'A' : 'B'}`"
                                                                                    >
                                                                                </v-col>
                                                                            </v-row>
                                                                        </v-container>
                                                                    </v-carousel-item>
                                                                </v-carousel>
                                                            </v-container>
                                                        </v-col>
                                                        <v-col v-if="index % 2 === 0" cols="1">
                                                            <v-divider vertical />
                                                        </v-col>
                                                    </v-row>
                                                </v-col>
                                            </template>
                                            <v-col v-else cols="12">
                                                <v-alert type="info" text border="left">
                                                    Brak podglądów.
                                                </v-alert>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-card>
                                <v-toolbar flat dense color="white lighten-2">
                                    <v-toolbar-title>Makieta</v-toolbar-title>
                                </v-toolbar>
                                <v-divider />
                                <v-card-text>
                                    <v-container v-viewer class="pa-0">
                                        <v-row dense>
                                            <template v-if="hasMockupPreviews">
                                                <v-col
                                                    v-for="(components, key, index) in currentPlateMockupPreviews"
                                                    :key="index"
                                                    cols="12"
                                                >
                                                    <v-row>
                                                        <v-col cols="11">
                                                            <v-container>
                                                                <v-carousel
                                                                    v-model="carouselMockupsIndexes[index]"
                                                                    height="auto"
                                                                    hide-delimiters
                                                                    :show-arrows="false"
                                                                    :continuous="false"
                                                                    :dark="$vuetify.theme.dark"
                                                                    :light="!$vuetify.theme.dark"
                                                                    :touchless="true"
                                                                >
                                                                    <v-carousel-item
                                                                        v-for="(image, previewIndex) in components"
                                                                        :key="previewIndex"
                                                                    >
                                                                        <v-container>
                                                                            <v-row justify="space-between">
                                                                                <v-icon :disabled="!(previewIndex > 0)" @click="prev(index, true)">
                                                                                    mdi-chevron-left-circle
                                                                                </v-icon>
                                                                                <v-icon :disabled="!(previewIndex + 3 < components.length - 1)" @click="next(index, true)">
                                                                                    mdi-chevron-right-circle
                                                                                </v-icon>
                                                                            </v-row>
                                                                            <v-row dense>
                                                                                <v-col cols="3">
                                                                                    <img
                                                                                        :src="taskPreviewURI(image)"
                                                                                        class="report-preview-img"
                                                                                        :alt="`Makieta ${previewIndex + 1}`"
                                                                                    >
                                                                                </v-col>
                                                                                <v-col v-if="previewIndex + 1 < components.length" cols="3">
                                                                                    <img
                                                                                        :src="taskPreviewURI(components[previewIndex + 1])"
                                                                                        class="report-preview-img"
                                                                                        :alt="`Makieta ${previewIndex + 2}`"
                                                                                    >
                                                                                </v-col>
                                                                                <v-col v-if="previewIndex + 2 < components.length" cols="3">
                                                                                    <img
                                                                                        :src="taskPreviewURI(components[previewIndex + 2])"
                                                                                        class="report-preview-img"
                                                                                        :alt="`Makieta ${previewIndex + 3}`"
                                                                                    >
                                                                                </v-col>
                                                                                <v-col v-if="previewIndex + 3 < components.length" cols="3">
                                                                                    <img
                                                                                        :src="taskPreviewURI(components[previewIndex + 3])"
                                                                                        class="report-preview-img"
                                                                                        :alt="`Makieta ${previewIndex + 4}`"
                                                                                    >
                                                                                </v-col>
                                                                            </v-row>
                                                                        </v-container>
                                                                    </v-carousel-item>
                                                                </v-carousel>
                                                            </v-container>
                                                        </v-col>
                                                    </v-row>
                                                </v-col>
                                            </template>
                                            <v-col v-else cols="12">
                                                <v-alert
                                                    type="info"
                                                    text
                                                    border="left"
                                                >
                                                    Brak podglądów makiet.
                                                </v-alert>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import ImagePreviewDialog from '@/components/common/ImagePreviewDialog'

import PageTitle from '@/components/common/PageTitle'
import OrdersListDialog from './OrdersListDialog'
import PalletsDialog from './PalletsDialog'
import ReportMaterialReservationDialog from './ReportMaterialReservationDialog'
import decodeQRcode from '@/mixins/decodeQRcode'
import tooltipDelay from '@/mixins/tooltip'
import { apiConfig } from '@/config'
import { errorHandler } from '@/utils/errorHandler'
import parse from '@/mixins/parse'

import { groupBy, sortBy } from 'lodash'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'

const CUT_OUT_ELASTIC_PROCESS_ID = 130

export default {
    name: 'CreateReport',
    components: {
        ConfirmDialog,
        ImagePreviewDialog,
        PageTitle,
        OrdersListDialog,
        PalletsDialog,
        ReportMaterialReservationDialog,
    },
    mixins: [decodeQRcode, parse, tooltipDelay],
    beforeRouteEnter(to, from, next) {
        if (typeof to.params.task !== 'undefined') {
            next(vm => {
                vm.task = to.params.task
                vm.currentPlate = to.params.plate
                vm.lastParameter = to.params.lastParameter
                vm.carouselIndexes = new Array(groupBy(vm.currentPlate.previews, 'type').length).fill(0)
                vm.carouselMockupsIndexes = new Array(groupBy(vm.currentPlate.mockupPreviews, 'type').length).fill(0)
                vm.outOfSequenceCodeId = to.params.outOfSequenceCodeId
                vm.showComments()
                vm.showLastPalletDialog()
            })
        } else {
            next({ name: 'reportsReporting' })
        }
    },
    data() {
        return {
            task: {
                type: {},
                typeId: null,
                reportItemPlates: [],
                primaryNumber: ''
            },
            currentPlate: {
                reportItemPlateParameters: [],
                wareStoragePlaces: [],
                reportItemPlateOrders: []
            },
            lastParameter: null,
            startTrackingLoading: false,
            addPalletLoading: false,
            imagePreviewDialog: false,
            palletsListDialog: false,
            lastPalletDialog: false,
            materialReservationDialog: false,
            filePreviewURI: null,
            areTaskCommentsVisible: true,
            areOrderCommentsVisible: true,
            outOfSequenceCodeId: null,
            carouselIndexes: [],
            carouselMockupsIndexes: [],
            ordersListDialog: false,
            selectAll: true,
            additionalActionsProcessProductionSite: [],
            pallets: [],
            additionalActionsProcessProductionSiteHeaders: [
                {
                    text: 'Wykonane czynności',
                    value: 'name',
                    sortable: false
                }
            ],
            existingReportId: null
        }
    },
    computed: {
        processName() {
            return this.reportSettings?.process?.name || ''
        },
        machineName() {
            return this.reportSettings?.machine?.name || ''
        },
        taskParameters() {
            const sortedList = [
                'format',
                'objętość',
                'składki',
                'rodzaj oprawy',
                'papier wnętrz',
                'uszlachetnianie wnętrz',
                'papier okładki',
                'uszlachetnianie okładki',
            ]

            return sortBy(this.currentPlate.reportItemPlateParameters, item => {
                return sortedList.indexOf(item.name)
            })
        },
        taskProductTypeName() {
            return this.task?.productType ? this.task.productType.name : ''
        },
        userId() {
            return this.$store.state.user.userId
        },
        reportSettings() {
            return this.$store.state.reports.reporting.reportSettings
        },
        plates() {
            const plates = this.task.reportItemPlates.filter(item => !item.isFullReported)

            if (this.reportSettings.process.id === CUT_OUT_ELASTIC_PROCESS_ID) {

                return plates.filter(item => item.hasNewElastic)
            }

            return plates
        },
        plateVerboseName() {
            return this.task.typeId === 60 ? 'Składka' : 'Montaż'
        },
        hasMultiplePlates() {
            return this.task.reportItemPlates.length > 1
        },
        isTypeIdValid() {
            return [15, 40, 50, 60].includes(this.task.typeId) // orders only
        },
        hasLocalization() {
            return this.currentPlate.wareStoragePlaces && this.currentPlate.wareStoragePlaces.length > 0
        },
        hasPreviews() {
            return this.currentPlate.previews && this.currentPlate.previews.length > 0
        },
        hasMockupPreviews() {
            return this.currentPlate.mockupPreviews && this.currentPlate.mockupPreviews.length > 0
        },
        currentPlatePreviews() {
            return groupBy(this.currentPlate.previews, 'type')
        },
        currentPlateMockupPreviews() {
            return groupBy(this.currentPlate.mockupPreviews, 'type')
        },
        hasTaskComments() {
            return this.currentPlate.comments ? this.currentPlate.comments.length > 0 : false
        },
        currentPlateWareStoragePlaces() {
            const placesHelper = {}

            const result = this.currentPlate.wareStoragePlaces.reduce((places, item) => {
                const key = `${item.hallId}-${item.ordinalNumber}`

                if (!placesHelper[key]) {
                    placesHelper[key] = { ...item, count: 1 }

                    places.push(placesHelper[key])
                } else {
                    placesHelper[key].count++
                }

                return places
            }, [])

            return result
        },
        reportStartButtonText() {
            return this.reportSettings.workspace.makeReadyRequired === true ? 'Rozpocznij przyrząd' : 'Rozpocznij'
        },
        isPalletRequired() {
            return this.reportSettings.workspace.isPalletNumberRequired
        },
        hasNoPallets() {
            return this.pallets.length < 1
        },
        addedPaperPallets() {
            return this.pallets.length.toString()
        },
        isStartButtonDisabledBase() {
            return this.hasTaskComments && this.areTaskCommentsVisible
        },
        isStartButtonDisabledDigitalPrint() {
            return this.reportSettings.workspace.machine.digitalPrinting && this.reportSettings.workspace.isPalletNumberRequired && this.hasNoPallets
        },
        isStartButtonDisabled() {
            return this.isStartButtonDisabledBase || this.isStartButtonDisabledDigitalPrint
        }
    },
    mounted() {
        this.$store.dispatch('reports/getAdditionalActionProcessProductionSite', {
            productionSiteId: this.reportSettings.workspace.id,
            processId: this.reportSettings.process.id
        })
            .then(response => {
                this.additionalActionsProcessProductionSite = response.data.results
            })
            .catch(error => {
                errorHandler(error)
            })
    },
    created() {
        document.addEventListener('keydown', this.QRtoJSON)
    },
    destroyed() {
        document.removeEventListener('keydown', this.QRtoJSON)
    },
    methods: {
        addPalletEanToList() {
            this.addPalletLoading = true
            this.pallets.push({ 'parameter': this.lastParameter })
            this.closeLastPalletDialog()
        },
        updateSelectAll() {
            this.selectAll = this.additionalActionsProcessProductionSite.filter(action =>
                action.isSelected === true).length === this.additionalActionsProcessProductionSite.length
        },
        toggleSelectAll(selectAllCheckbox) {
            this.selectAll = selectAllCheckbox
            this.additionalActionsProcessProductionSite.forEach(item => {
                item.isSelected = this.selectAll
            })
        },
        prev(index, carouselMockup = false, number = 4) {
            if (!carouselMockup) {
                return this.carouselIndexes.splice(index, 1, this.carouselIndexes[index] - 1)
            } else {
                return this.carouselMockupsIndexes.splice(index, 1, this.carouselMockupsIndexes[index] - number)
            }
        },
        next(index, carouselMockup = false, number = 4) {
            if (!carouselMockup) {
                return this.carouselIndexes.splice(index, 1, this.carouselIndexes[index] + 1)
            } else {
                return this.carouselMockupsIndexes.splice(index, 1, this.carouselMockupsIndexes[index] + number)
            }
        },
        createReport() {
            this.startTrackingLoading = true
            this.$store.dispatch('reports/createReport', {
                processCodeId: 1,
                objectId: this.currentPlate.id,
                objectType: this.currentPlate.objectType,
                objectNumber: this.task.primaryNumber,
                plateNumber: this.currentPlate.plateNumber || 1,
                machine: this.reportSettings.machine.id,
                process: this.reportSettings.process.id,
                user: this.userId,
                outOfSequenceCodeId: this.outOfSequenceCodeId,
                plannedDuration: this.currentPlate.duration,
                currentlyReportedQuantity: 0,
                productionSiteId: this.reportSettings.workspace.id,
                additionalActionProcessProductionSites: JSON.parse(JSON.stringify(this.additionalActionsProcessProductionSite)),
                startMakeReady: this.reportSettings.workspace.makeReadyRequired === true,
                reportPalletsData: this.pallets
            })
                .then(response => {
                    this.$router.push(
                        {
                            name: 'reportsReportingReport',
                            params: {
                                id: response.id,
                                report: response
                            }
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                })
                .catch(error => {
                    this.startTrackingLoading = false
                    errorHandler(error)
                })
        },
        taskPreviewURI(image) {
            const mediaRoot = apiConfig.FACTORYBASE_API_URI

            return `${mediaRoot}${image.path}`
        },
        showFilePreviewDialog(image) {
            this.filePreviewURI = this.taskPreviewURI(image)
            this.imagePreviewDialog = true
        },
        closeImagePreviewDialog() {
            this.imagePreviewDialog = false
        },
        handleQrJSON({ data }) {
            if (data.event === 'start') {
                this.createReport()
            } else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('reports.messages.unsupportedQrCode')
                })
            }
        },
        confirmTaskCommentsRead() {
            this.areTaskCommentsVisible = false
        },
        showComments() {
            if (this.currentPlate) {
                this.areCommentsVisible = this.currentPlate.comments.length > 0
            }
        },
        showLastPalletDialog() {
            if (this.lastParameter && this.reportSettings.workspace.isPalletNumberRequired) {
                this.lastPalletDialog = true
            }
        },
        closeLastPalletDialog() {
            this.lastPalletDialog = false
            this.addPalletLoading = false
        },
        showOrdersListDialog() {
            this.ordersListDialog = true
        },
        closeOrdersListDialog() {
            this.ordersListDialog = false
        },
        close() {
            this.$router.push({
                name: 'reportsReporting',
                params: {
                    machine: this.reportSettings.machine,
                    process: this.reportSettings.process
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showPalletsListDialog() {
            this.palletsListDialog = true
        },
        closePalletsListDialog() {
            this.palletsListDialog = false
        },
        showMaterialReservationDialog() {
            this.materialReservationDialog = true
        },
        closeMaterialReservationDialog() {
            this.materialReservationDialog = false
        },
    }
}
</script>

<style scoped>
.comment-metadata {
    font-size: 12px;
    opacity: 0.8;
    margin-top: 2px;
    font-style: italic;
}
.report-preview-img {
    max-height: 350px;
    width: 100%;
    object-fit: contain;
    cursor: pointer;
    display: block;
}
</style>